import apiService from './apiService';
import { API_ENDPOINTS } from '../config/api';
import favoriteService from './favoriteService';
import reviewService from './reviewService';
import notificationService from './notificationService';

class DashboardService {

  // Alap dashboard adatok felhasználó típus szerint
  async getBaseDashboardData(userType) {
    try {
      console.log('getBaseDashboardData called with userType:', userType);
      
      const normalizedUserType = userType?.toUpperCase();
      
      switch (normalizedUserType) {
        case 'ADMIN':
          return await this.getAdminDashboardData();
        case 'PROVIDER':
          return await this.getProviderDashboardData();
        case 'CLIENT':
        case 'USER':
          return await this.getClientDashboardData();
        default:
          console.warn('Ismeretlen felhasználó típus:', userType, 'Fallback CLIENT-re');
          return await this.getClientDashboardData();
      }
    } catch (error) {
      console.error('Alap dashboard adatok hiba:', error);
      throw error;
    }
  }

  // Ügyfél dashboard adatok - JAVÍTOTT
  async getClientDashboardData() {
    try {
      console.log('Loading client dashboard data...');
      
      let recentBookings = [];
      try {
        console.log('Fetching recent bookings...');
        const bookingsResponse = await apiService.get('/bookings?limit=5');
        console.log('Raw bookings response:', bookingsResponse);
        
        // A backend {success: true, data: {bookings: [], pagination: {}}} formátumban ad vissza
        if (bookingsResponse.success && bookingsResponse.data) {
          if (Array.isArray(bookingsResponse.data.bookings)) {
            recentBookings = bookingsResponse.data.bookings;
          } else if (Array.isArray(bookingsResponse.data)) {
            recentBookings = bookingsResponse.data;
          }
        } else if (Array.isArray(bookingsResponse)) {
          recentBookings = bookingsResponse;
        }
        
        console.log('Processed recentBookings:', recentBookings);
      } catch (error) {
        console.error('Foglalások betöltése hiba:', error);
        recentBookings = [];
      }

      // Biztosítsuk, hogy recentBookings tömb legyen
      if (!Array.isArray(recentBookings)) {
        console.warn('recentBookings is not an array, converting to empty array');
        recentBookings = [];
      }

      return {
        type: 'client',
        stats: {
          totalBookings: recentBookings.length,
          pendingBookings: recentBookings.filter(b => b.status === 'PENDING').length,
          completedBookings: recentBookings.filter(b => b.status === 'COMPLETED').length
        },
        recentBookings: recentBookings
      };
    } catch (error) {
      console.error('Ügyfél dashboard adatok hiba:', error);
      throw error;
    }
  }

  // Szolgáltató dashboard adatok - JAVÍTOTT
  async getProviderDashboardData() {
    try {
      console.log('Loading provider dashboard data...');
      
      let recentBookings = [];
      let stats = {
        totalBookings: 0,
        pendingBookings: 0,
        completedBookings: 0,
        totalRevenue: 0,
        averageRating: 0,
        totalReviews: 0
      };

      try {
        console.log('Fetching provider bookings...');
        const bookingsResponse = await apiService.get('/bookings?limit=5');
        console.log('Provider bookings response:', bookingsResponse);
        
        // A backend {success: true, data: {bookings: [], pagination: {}}} formátumban ad vissza
        if (bookingsResponse.success && bookingsResponse.data) {
          if (Array.isArray(bookingsResponse.data.bookings)) {
            recentBookings = bookingsResponse.data.bookings;
          } else if (Array.isArray(bookingsResponse.data)) {
            recentBookings = bookingsResponse.data;
          }
        } else if (Array.isArray(bookingsResponse)) {
          recentBookings = bookingsResponse;
        }
        
        if (!Array.isArray(recentBookings)) {
          recentBookings = [];
        }
        
        stats.totalBookings = recentBookings.length;
        stats.pendingBookings = recentBookings.filter(b => b.status === 'PENDING').length;
        stats.completedBookings = recentBookings.filter(b => b.status === 'COMPLETED').length;
      } catch (error) {
        console.error('Szolgáltató foglalások betöltése hiba:', error);
      }

      return {
        type: 'provider',
        stats,
        recentBookings: recentBookings
      };
    } catch (error) {
      console.error('Szolgáltató dashboard adatok hiba:', error);
      throw error;
    }
  }

  // Admin dashboard adatok
  async getAdminDashboardData() {
    try {
      console.log('Loading admin dashboard data...');
      
      // MÓDOSÍTVA: Eltávolítjuk a getRecentActivity hívását
      const [stats] = await Promise.all([
        this.getAdminStats(),
        // this.getRecentActivity() // Ezt a sort kommenteld ki vagy töröld
      ]);

      return {
        type: 'admin',
        stats,
        // recentActivity // Ezt a sort is kommenteld ki vagy töröld
      };
    } catch (error) {
      console.error('Admin dashboard adatok hiba:', error);
      throw error;
    }
  }

  // Szolgáltató statisztikák
  async getProviderStats() {
    try {
      const response = await apiService.get('/providers/me/stats');
      return response;
    } catch (error) {
      console.error('Szolgáltató statisztikák hiba:', error);
      return {
        totalBookings: 0,
        pendingBookings: 0,
        completedBookings: 0,
        totalRevenue: 0,
        averageRating: 0,
        totalReviews: 0
      };
    }
  }

  // Admin statisztikák
  async getAdminStats() {
    try {
      const response = await apiService.get(API_ENDPOINTS.STATS.PLATFORM);
      return response;
    } catch (error) {
      console.error('Admin statisztikák hiba:', error);
      return {
        totalUsers: 0,
        totalProviders: 0,
        totalBookings: 0,
        totalRevenue: 0,
        pendingProviders: 0
      };
    }
  }

  // Legutóbbi tevékenységek
  async getRecentActivity() {
    try {
      const response = await apiService.get('/dashboard/recent-activity');
      return response;
    } catch (error) {
      console.error('Legutóbbi tevékenységek hiba:', error);
      return [];
    }
  }

  // Kedvencek dashboard adatok - JAVÍTOTT
  async getFavoritesDashboardData() {
    try {
      console.log('Loading favorites dashboard data...');
      const response = await favoriteService.getUserFavorites(1, 5);
      console.log('Favorites response:', response);
      
      let favorites = [];
      let total = 0;
      
      // A válasz: {favorites: [], pagination: {total: 0}}
      if (response && typeof response === 'object') {
        if (Array.isArray(response.favorites)) {
          favorites = response.favorites;
          total = response.pagination?.total || response.favorites.length;
        } else if (Array.isArray(response)) {
          favorites = response;
          total = response.length;
        }
      }
      
      console.log('Processed favorites:', favorites, 'total:', total);
      
      return {
        recentFavorites: favorites,
        totalFavorites: total
      };
    } catch (error) {
      console.error('Kedvencek dashboard adatok hiba:', error);
      return {
        recentFavorites: [],
        totalFavorites: 0
      };
    }
  }

  // Értesítések dashboard adatok - JAVÍTOTT
  async getNotificationsDashboardData() {
    try {
      console.log('Loading notifications dashboard data...');
      
      const [notificationsResponse, unreadResponse] = await Promise.all([
        notificationService.getUserNotifications(1, 5),
        notificationService.getUnreadCount()
      ]);

      console.log('Notifications response:', notificationsResponse);
      console.log('Unread response:', unreadResponse);

      let notifications = [];
      let total = 0;
      let unreadCount = 0;

      // Notifications feldolgozása: {notifications: [], unreadCount: 0, pagination: {}}
      if (notificationsResponse && typeof notificationsResponse === 'object') {
        if (Array.isArray(notificationsResponse.notifications)) {
          notifications = notificationsResponse.notifications;
          total = notificationsResponse.pagination?.total || notificationsResponse.notifications.length;
        } else if (Array.isArray(notificationsResponse)) {
          notifications = notificationsResponse;
          total = notificationsResponse.length;
        }
      }

      // Unread count feldolgozása: {unreadCount: 0}
      if (typeof unreadResponse === 'number') {
        unreadCount = unreadResponse;
      } else if (unreadResponse && typeof unreadResponse === 'object' && unreadResponse.unreadCount !== undefined) {
        unreadCount = unreadResponse.unreadCount;
      }

      console.log('Processed notifications:', notifications, 'total:', total, 'unread:', unreadCount);

      return {
        recentNotifications: notifications,
        totalNotifications: total,
        unreadCount: unreadCount
      };
    } catch (error) {
      console.error('Értesítések dashboard adatok hiba:', error);
      return {
        recentNotifications: [],
        totalNotifications: 0,
        unreadCount: 0
      };
    }
  }

  // Értékelések dashboard adatok - JAVÍTOTT
  async getReviewsDashboardData() { // Eltávolítva a userId paraméter, mert a getMyReviews nem használja
    try {
      console.log('Loading reviews dashboard data...');
      // MÓDOSÍTVA: reviewService.getMyReviews() hívása a megfelelő paraméterekkel
      const response = await reviewService.getMyReviews({ page: 1, limit: 5 }); 
      console.log('Reviews response:', response);
      
      let reviews = [];
      let total = 0;
      
      if (response && typeof response === 'object') {
        if (Array.isArray(response.reviews)) {
          reviews = response.reviews;
          total = response.pagination?.total || response.reviews.length;
        } else if (Array.isArray(response)) {
          reviews = response;
          total = response.length;
        }
      }
      
      return {
        recentReviews: reviews,
        totalReviews: total
      };
    } catch (error) {
      console.error('Értékelések dashboard adatok hiba:', error);
      return {
        recentReviews: [],
        totalReviews: 0
      };
    }
  }

  // Teljes dashboard adatok
  async getDashboardData(userType) {
    try {
      console.log('getDashboardData called with userType:', userType);
      
      const baseData = await this.getBaseDashboardData(userType);
      console.log('Base data loaded:', baseData);
      
      const normalizedUserType = userType?.toUpperCase();
      
      // Új adatok hozzáadása (csak CLIENT és PROVIDER esetén)
      if (normalizedUserType === 'CLIENT' || normalizedUserType === 'USER' || normalizedUserType === 'PROVIDER') {
        console.log('Loading additional data for client/provider...');
        
        const [favoritesData, notificationsData, reviewsData] = await Promise.all([
          this.getFavoritesDashboardData().catch(err => {
            console.error('Favorites error:', err);
            return { recentFavorites: [], totalFavorites: 0 };
          }),
          this.getNotificationsDashboardData().catch(err => {
            console.error('Notifications error:', err);
            return { recentNotifications: [], totalNotifications: 0, unreadCount: 0 };
          }),
          // MÓDOSÍTVA: A getReviewsDashboardData már nem vár userId-t
          this.getReviewsDashboardData().catch(err => { 
            console.error('Reviews error:', err);
            return { recentReviews: [], totalReviews: 0 };
          })
        ]);

        console.log('Additional data loaded:', { favoritesData, notificationsData, reviewsData });

        return {
          ...baseData,
          favorites: favoritesData,
          notifications: notificationsData,
          reviews: reviewsData
        };
      }

      // Admin esetén csak értesítések
      if (normalizedUserType === 'ADMIN') {
        const notificationsData = await this.getNotificationsDashboardData().catch(err => {
          console.error('Admin notifications error:', err);
          return { recentNotifications: [], totalNotifications: 0, unreadCount: 0 };
        });
        
        return {
          ...baseData,
          notifications: notificationsData
        };
      }

      return baseData;
    } catch (error) {
      console.error('Dashboard adatok betöltése hiba:', error);
      throw error;
    }
  }


  // Gyors statisztikák (header-hez)
  async getQuickStats(userType) {
    try {
      const normalizedUserType = userType?.toUpperCase();
      
      switch (normalizedUserType) {
        case 'CLIENT':
        case 'USER':
          const [favoritesData, notificationsData] = await Promise.all([
            this.getFavoritesDashboardData(),
            this.getNotificationsDashboardData()
          ]);
          return {
            favorites: favoritesData.totalFavorites,
            unreadNotifications: notificationsData.unreadCount
          };
        
        case 'PROVIDER':
          const [providerStats, providerNotifications] = await Promise.all([
            this.getProviderStats(),
            this.getNotificationsDashboardData()
          ]);
          return {
            pendingBookings: providerStats.pendingBookings || 0,
            unreadNotifications: providerNotifications.unreadCount
          };
        
        case 'ADMIN':
          const [adminStats, adminNotifications] = await Promise.all([
            this.getAdminStats(),
            this.getNotificationsDashboardData()
          ]);
          return {
            pendingProviders: adminStats.pendingProviders || 0,
            unreadNotifications: adminNotifications.unreadCount
          };
        
        default:
          return {};
      }
    } catch (error) {
      console.error('Gyors statisztikák hiba:', error);
      return {};
    }
  }
}

const dashboardService = new DashboardService();
export default dashboardService;